import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  HStack,
  Icon,
  Link,
  ListItem,
  Spinner,
  Stack,
  Text,
  UnorderedList,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { IconType } from "react-icons";
import { FaCheckCircle, FaFilePdf, FaRegFileAlt } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import {
  PreflightRequirementDto,
  PreflightWarningDto,
  PublicBidStatus,
  useCancelPublicBidSigningMutation,
  useGetPublicBidQuery,
  useGetPublicBidSigningPreflightQuery,
  useStartPublicBidSigningMutation,
  type PortalSourcingEventDetailDto,
  type PublicBidDto,
  type PublicBidPatchDto,
} from "../../../../../autogen/bff-api";
import { CancelSigningDialog } from "../CancelSigningDialog";
import { CompletedInsteadDialog } from "../CompletedInsteadDialog";
import { SigningPendingActions } from "../SigningPendingActions";
import { DownloadDocumentIconButton } from "../../../../../common/documents/DownloadDocumentIconButton/DownloadDocumentIconButton";
import { useApiError } from "../../../../../common/errors/useApiError";
import { getOrganizationId } from "../../../../../common/local-storage/selected-organization";
import { PreviewPdfModal } from "../../../../../common/documents/PreviewPdfModal";
import { getPusher } from "../../../../../common/pusher";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { coordinatorRegistry, useEditor } from "../../../../../common/editing/save-coordinator";
import { urls } from "../../../../../urls";
import { EventDeadlineHighlight } from "../../view-single/EventDeadlineHighlight";
import { emptyEspd } from "../espd/useEspdAnswers";
import { isPublicBidEditable } from "../publicBidEditability";
import { useBidStepNavigation } from "../useBidStepNavigation";

interface Props {
  eventId: string;
  bidId: string;
  bid: PublicBidDto;
  event: PortalSourcingEventDetailDto;
  previousStep?: () => void;
}

export const ReviewStep = ({
  eventId,
  bidId,
  bid,
  event,
  previousStep,
}: Props) => {
  const { t } = useTranslation();
  const apiError = useApiError();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const collectSupplierPrice = event.collectSupplierPrice ?? true;
  const { state, updateField, flushPending, entityStatus, fieldStatus } = useEditor<
    PublicBidDto,
    PublicBidPatchDto
  >(bidId);
  const currentBid = state.id ? state : bid;
  const { navigateToStep } = useBidStepNavigation(
    eventId,
    bidId,
    collectSupplierPrice,
  );
  const signingOutcome = searchParams.get("signering");

  const isSubmitted = currentBid.status === PublicBidStatus.Submitted;
  const readOnly = !isPublicBidEditable(currentBid);

  const { data: preflight } = useGetPublicBidSigningPreflightQuery(
    { bidId },
    { skip: isSubmitted },
  );
  const [startSigning, { isLoading: isStarting }] =
    useStartPublicBidSigningMutation();
  const [cancelSigning, { isLoading: isCanceling }] =
    useCancelPublicBidSigningMutation();
  const { refetch: refetchBid } = useGetPublicBidQuery({ bidId });

  const [previewUrl, setPreviewUrl] = useState<string>();
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [tookTooLong, setTookTooLong] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isCompletedInsteadOpen, setIsCompletedInsteadOpen] = useState(false);
  const [postSigningNotice, setPostSigningNotice] = useState<
    "supplier-canceled" | "signicat-canceled" | "expired" | null
  >(null);
  const espdResponse = currentBid.espdResponse ?? emptyEspd();
  const finalDeclarationConfirmed =
    espdResponse.finalDeclaration?.confirmed ?? false;
  const isSavingFinalDeclaration =
    fieldStatus("espdResponse") === "saving" ||
    fieldStatus("espdResponse") === "pending";
  const setFinalDeclarationConfirmed = (confirmed: boolean) => {
    if (readOnly) return;
    updateField(
      {
        espdResponse: {
          ...espdResponse,
          finalDeclaration: { confirmed },
        },
      },
      { debounceMs: 300 },
    );
  };

  // After Signicat redirects the supplier back, the BFF's signing webhook finalizes delivery and
  // emits a "bid-delivered" Pusher event on this bid's channel. We subscribe here so the page
  // flips to the submitted confirmation as soon as the bid lands, without polling.
  useEffect(() => {
    if (signingOutcome !== "fullfort" || isSubmitted) return;
    const channel = getPusher().subscribe(bidId);
    const onDelivered = () => {
      refetchBid();
    };
    channel.bind("bid-delivered", onDelivered);
    // Cover the race where the webhook finalized the bid before this effect bound the handler —
    // Pusher doesn't buffer public-channel events, so without this the spinner would sit until
    // the 30s warning. The handler is idempotent (RTK refetch).
    refetchBid();
    const timer = window.setTimeout(() => setTookTooLong(true), 30_000);
    return () => {
      channel.unbind("bid-delivered", onDelivered);
      window.clearTimeout(timer);
    };
  }, [signingOutcome, isSubmitted, bidId, refetchBid]);

  // Listen for server-side cancel and expiry events so the UI reflects the released bid
  // immediately — the wizard returns to editable Draft as soon as signedDocumentId clears.
  useEffect(() => {
    const channel = getPusher().subscribe(bidId);
    const onCanceled = () => {
      refetchBid();
      setPostSigningNotice("signicat-canceled");
      toast({
        title: t("Signing was cancelled"),
        description: t("Your bid is back in draft mode."),
        status: "info",
        duration: 5000,
        isClosable: true,
      });
    };
    const onExpired = () => {
      refetchBid();
      setPostSigningNotice("expired");
      toast({
        title: t("Signing request expired"),
        description: t("Your bid is back in draft mode."),
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    };
    channel.bind("bid-signing-canceled", onCanceled);
    channel.bind("bid-signing-expired", onExpired);
    return () => {
      channel.unbind("bid-signing-canceled", onCanceled);
      channel.unbind("bid-signing-expired", onExpired);
    };
  }, [bidId, refetchBid, toast, t]);

  // Object URLs hold the PDF bytes alive until revoked. This cleanup runs whenever previewUrl
  // changes (releasing the old one) and on unmount, so no URL is ever leaked.
  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  const openPreview = async () => {
    setIsPreviewLoading(true);
    try {
      const organizationId = getOrganizationId();
      const response = await fetch(
        `${import.meta.env.VITE_BFF_BASE_URL}/api/portal/bids/${bidId}/report`,
        {
          credentials: "include",
          headers: organizationId ? { organizationId } : {},
        },
      );
      if (!response.ok)
        throw new Error(`Report preview failed with status ${response.status}`);
      const blob = await response.blob();
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (e) {
      apiError.trigger(e);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleSignAndDeliver = async () => {
    await flushPending();
    if (coordinatorRegistry.get(bidId)?.getEntityStatus() !== "saved") return;

    const path = urls.portal.bid.edit.go(eventId, bidId).review;
    const base = `${window.location.origin}${path.pathname}?${path.search}`;
    try {
      const { signingUrl } = await startSigning({
        bidId,
        startPublicBidSigningRequestDto: {
          successUrl: `${base}&signering=fullfort`,
          cancelUrl: `${base}&signering=avbrutt`,
          errorUrl: `${base}&signering=feilet`,
        },
      }).unwrap();
      window.location.href = signingUrl;
    } catch (e) {
      apiError.trigger(e);
    }
  };

  const handleContinueSigning = async () => {
    // Continue-aware startSigning: backend recognises the existing Signing revision and returns
    // the same Signicat URL instead of creating a new order.
    const path = urls.portal.bid.edit.go(eventId, bidId).review;
    const base = `${window.location.origin}${path.pathname}?${path.search}`;
    try {
      const { signingUrl } = await startSigning({
        bidId,
        startPublicBidSigningRequestDto: {
          successUrl: `${base}&signering=fullfort`,
          cancelUrl: `${base}&signering=avbrutt`,
          errorUrl: `${base}&signering=feilet`,
        },
      }).unwrap();
      window.location.href = signingUrl;
    } catch (e) {
      apiError.trigger(e);
    }
  };

  const handleConfirmCancel = async () => {
    try {
      const result = await cancelSigning({
        bidId,
        cancelPublicBidSigningRequestDto: {
          reason: "Supplier-initiated cancel from review step",
        },
      }).unwrap();
      setIsCancelDialogOpen(false);
      if (result.outcome === "CompletedInstead") {
        setIsCompletedInsteadOpen(true);
      } else {
        refetchBid();
        setPostSigningNotice("supplier-canceled");
      }
    } catch (e) {
      apiError.trigger(e);
    }
  };

  const handleAcknowledgeCompletedInstead = () => {
    const path = urls.portal.bid.edit.go(eventId, bidId).review;
    window.location.href = `${path.pathname}?${path.search}&signering=fullfort`;
  };

  if (isSubmitted) {
    const submittedTime = currentBid.submittedAt
      ? new Date(currentBid.submittedAt).toLocaleTimeString(undefined, {
          hour: "2-digit",
          minute: "2-digit",
        })
      : null;
    return (
      <Box pb="8" pt="10">
        <Stack
          spacing="4"
          align="center"
          textAlign="center"
          borderWidth="1px"
          borderColor="smBorder"
          borderRadius="lg"
          py="16"
          px="8"
        >
          <Icon as={FaCheckCircle} boxSize="10" color="smPrimary" />
          <Heading size="md" color="smPrimary">
            {submittedTime
              ? t("The offer was delivered at {{time}}.", {
                  time: submittedTime,
                })
              : t("The offer has been submitted")}
          </Heading>
          <Text fontSize="sm" color="smMuted">
            {t("You will receive an email receipt shortly.")}
          </Text>
          <Text fontSize="sm" color="smMuted">
            {t("Reference: {{reference}}", { reference: currentBid.id })}
          </Text>
          <Text fontSize="sm" color="smMuted">
            {t("The offer is now locked and can no longer be edited.")}
          </Text>
          <Stack spacing="1" align="center">
            <Text
              fontWeight="semibold"
              color="smMuted"
              textDecoration="underline"
              cursor="not-allowed"
            >
              {t("See the delivered offer")}
            </Text>
            <Text fontSize="xs" color="smMuted">
              {t(
                "Detailed view of the delivered offer is coming in a later update.",
              )}
            </Text>
          </Stack>
        </Stack>
      </Box>
    );
  }

  if (signingOutcome === "fullfort") {
    return (
      <Box pb="8" pt="10">
        <Stack spacing="4" align="center" textAlign="center" py="16">
          <Spinner color="smPrimary" />
          <Text color="smMuted">
            {t("Signing complete — confirming delivery…")}
          </Text>
          {tookTooLong && (
            <Alert status="warning" borderRadius="lg" maxW="md">
              <AlertIcon />
              <Text fontSize="sm">
                {t(
                  "This is taking longer than usual. Reload the page if it doesn't resolve.",
                )}
              </Text>
            </Alert>
          )}
        </Stack>
      </Box>
    );
  }

  const isSigningStarted =
    currentBid.status === PublicBidStatus.Draft && currentBid.signedDocumentId != null;
  // When signing is in flight the state is communicated by SigningPendingActions and the
  // pill in the wizard header, not by the preflight banner. Filter the signal out and skip
  // the other remaining items too — the supplier can't act on them while content is locked.
  const remaining = isSigningStarted
    ? []
    : (preflight?.remaining ?? []).filter(
        (r) => r !== PreflightRequirementDto.SigningInProgress,
      );
  const warnings = preflight?.warnings ?? [];
  const isReady = preflight?.ready ?? false;
  const hasPendingSave = entityStatus === "pending" || entityStatus === "saving";
  const hasSaveError = entityStatus === "error";
  const documents = currentBid.documents ?? [];

  // URL-based return notices from Signicat redirects. These take precedence over Pusher-driven
  // notices on first paint, but get cleared once we transition out of isSigningStarted.
  const urlNotice: typeof postSigningNotice =
    !isSigningStarted && signingOutcome === "avbrutt"
      ? "signicat-canceled"
      : !isSigningStarted && signingOutcome === "utlopt"
        ? "expired"
        : null;
  const activeNotice = postSigningNotice ?? urlNotice;

  return (
    <Box pb="8" pt="6">
      <Stack spacing="2" mb="6">
        <Heading size="md">{t("Summary and submission")}</Heading>
        <Text fontSize="sm" color="smMuted">
          {t("Preview the report and sign your offer with BankID.")}
        </Text>
      </Stack>

      <Stack spacing="6" mb="8">
        <EventDeadlineHighlight event={event} />

        {activeNotice && (
          <Alert
            status={activeNotice === "expired" ? "warning" : "info"}
            borderRadius="lg"
            alignItems="flex-start"
          >
            <AlertIcon />
            <Stack spacing="1">
              <Text fontSize="sm" fontWeight="semibold">
                {activeNotice === "supplier-canceled"
                  ? t("Signing was cancelled")
                  : activeNotice === "signicat-canceled"
                    ? t("Signing was cancelled in BankID")
                    : t("Signing request expired")}
              </Text>
              <Text fontSize="sm">
                {t(
                  "Your changes are preserved. Edit if needed, then sign again.",
                )}
              </Text>
            </Stack>
          </Alert>
        )}
        {signingOutcome === "feilet" && !isSigningStarted && (
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            <Text fontSize="sm">{t("Signing failed. You can try again.")}</Text>
          </Alert>
        )}

        {remaining.length > 0 && (
          <Alert status="error" borderRadius="lg" alignItems="flex-start">
            <AlertIcon />
            <Stack spacing="2">
              <Text fontSize="sm" fontWeight="semibold">
                {t("Complete the following before you can deliver:")}
              </Text>
              <Stack spacing="1">
                {remaining.map((requirement) => (
                  <RemainingRequirement
                    key={requirement}
                    requirement={requirement}
                    onJumpToSupplier={() => navigateToStep("supplier")}
                    onJumpToEspd={() => navigateToStep("exclusion")}
                    onJumpToQualification={() =>
                      navigateToStep("qualification")
                    }
                  />
                ))}
              </Stack>
            </Stack>
          </Alert>
        )}

        {warnings.includes(PreflightWarningDto.PriceMissing) && (
          <Alert status="info" borderRadius="lg" alignItems="flex-start">
            <AlertIcon />
            <Text fontSize="sm">
              {t(
                "No total price entered. The buyer may be unable to score your offer's price.",
              )}{" "}
              <Link color="smPrimary" onClick={() => navigateToStep("price")}>
                {t("Go to price")}
              </Link>
            </Text>
          </Alert>
        )}

        <ReviewSection
          icon={FaCheckCircle}
          title={t("ESPD Part VI - Final declarations")}
          description={t(
            "Confirm the final ESPD declarations before the offer is signed and delivered.",
          )}
          badgeLabel={
            finalDeclarationConfirmed ? t("Completed") : t("Required")
          }
          badgeTone={finalDeclarationConfirmed ? "success" : "warning"}
          rightContent={
            isSavingFinalDeclaration ? (
              <Spinner size="sm" color="smMuted" />
            ) : undefined
          }
        >
          <Box
            borderWidth="1px"
            borderColor="smBorder"
            borderRadius="md"
            bg="smBackgroundSecondary"
            p="3"
          >
            <UnorderedList spacing="1" pl="5">
              {[
                t("The supplier declares that the information in the ESPD is accurate and truthful, and set out with full awareness of the consequences of serious misrepresentation."),
                t("The supplier can, upon request and without delay, provide the certificates and other forms of documentary evidence referred to in the ESPD."),
                t("The supplier consents to the contracting authority accessing documents supporting the information provided in the ESPD for this procurement."),
              ].map((statement) => (
                <ListItem key={statement} fontSize="sm">
                  {statement}
                </ListItem>
              ))}
            </UnorderedList>
          </Box>
          <Box
            borderWidth="1px"
            borderColor={
              finalDeclarationConfirmed ? "smSuccessSoftBorder" : "smBorder"
            }
            borderRadius="md"
            bg={finalDeclarationConfirmed ? "smSuccessSoft" : "smBackground"}
            p="3"
          >
            <Checkbox
              colorScheme="teal"
              isChecked={finalDeclarationConfirmed}
              isDisabled={readOnly}
              onChange={(e) => setFinalDeclarationConfirmed(e.target.checked)}
              sx={{ ".chakra-checkbox__label": { opacity: 1 } }}
            >
              {t("I confirm the final ESPD declarations")}
            </Checkbox>
          </Box>
        </ReviewSection>

        <ReviewSection
          icon={FaRegFileAlt}
          title={t("Offer documents")}
          badgeLabel={t("{{count}} documents attached", {
            count: documents.length,
          })}
          badgeTone={documents.length > 0 ? "success" : "neutral"}
          rightContent={
            readOnly ? undefined : (
              <Link
                fontSize="sm"
                color="smPrimary"
                fontWeight="semibold"
                onClick={() => navigateToStep("documents")}
              >
                {t("Edit")}
              </Link>
            )
          }
        >
          {documents.length === 0 ? (
            <Box
              borderWidth="1px"
              borderColor="smBorder"
              borderRadius="md"
              bg="smBackgroundSecondary"
              p="3"
            >
              <Text fontSize="sm" color="smMuted">
                {t("No documents attached")}
              </Text>
            </Box>
          ) : (
            <Stack spacing="2">
              {documents.map((doc) => (
                <Flex
                  key={doc.id}
                  align={{ base: "stretch", md: "center" }}
                  justify="space-between"
                  direction={{ base: "column", md: "row" }}
                  gap="3"
                  borderWidth="1px"
                  borderColor="smBorder"
                  borderRadius="md"
                  bg="smBackgroundSecondary"
                  px="3"
                  py="2"
                >
                  <HStack spacing="2" minW="0">
                    <Icon
                      as={FaRegFileAlt}
                      color="smMuted"
                      boxSize="3.5"
                      flexShrink={0}
                    />
                    <Text fontSize="sm" noOfLines={1}>
                      {doc.name}
                    </Text>
                  </HStack>
                  <Box alignSelf={{ base: "flex-start", md: "center" }}>
                    <DownloadDocumentIconButton documentId={doc.id} size="sm" />
                  </Box>
                </Flex>
              ))}
            </Stack>
          )}
        </ReviewSection>

        <ReviewSection
          icon={FaFilePdf}
          title={t("Report")}
          rightContent={
            <Button
              leftIcon={<FaFilePdf />}
              variant="outline"
              size="sm"
              onClick={openPreview}
              isLoading={isPreviewLoading}
            >
              {t("Preview report")}
            </Button>
          }
        />
      </Stack>

      {isSigningStarted ? (
        <SigningPendingActions
          onPrevious={previousStep}
          onContinue={handleContinueSigning}
          onCancel={() => setIsCancelDialogOpen(true)}
          isContinuing={isStarting}
          isCanceling={isCanceling}
        />
      ) : (
        <StepNavigation
          onPrevious={previousStep}
          onNext={handleSignAndDeliver}
          nextLabel={t("Sign and deliver")}
          nextIsAction
          isNextLoading={isStarting || entityStatus === "saving"}
          isNextDisabled={!isReady || hasPendingSave || hasSaveError}
          nextTooltip={
            hasPendingSave
              ? t("Wait until the latest changes are saved.")
              : hasSaveError
                ? t("Resolve save errors before signing.")
                : undefined
          }
        />
      )}

      <CancelSigningDialog
        isOpen={isCancelDialogOpen}
        onClose={() => setIsCancelDialogOpen(false)}
        onConfirm={handleConfirmCancel}
        isSubmitting={isCanceling}
      />

      <CompletedInsteadDialog
        isOpen={isCompletedInsteadOpen}
        onAcknowledge={handleAcknowledgeCompletedInstead}
      />

      {previewUrl && (
        <PreviewPdfModal
          url={previewUrl}
          onClose={() => setPreviewUrl(undefined)}
        />
      )}
    </Box>
  );
};

const badgeStyles = {
  success: { bg: "smSuccessSoft", color: "smSuccess" },
  warning: { bg: "smWarningSoft", color: "smWarning" },
  neutral: { bg: "smBackgroundSecondary", color: "smMuted" },
};

const ReviewSection = ({
  icon,
  title,
  description,
  badgeLabel,
  badgeTone,
  rightContent,
  children,
}: {
  icon: IconType;
  title: ReactNode;
  description?: ReactNode;
  badgeLabel?: ReactNode;
  badgeTone?: keyof typeof badgeStyles;
  rightContent?: ReactNode;
  children?: ReactNode;
}) => {
  const badgeStyle = badgeTone ? badgeStyles[badgeTone] : undefined;
  const hasHeaderMeta = Boolean(badgeLabel || rightContent);
  return (
    <Box
      borderWidth="1px"
      borderColor="smBorder"
      borderRadius="lg"
      bg="smBackground"
      boxShadow="sm"
      p={{ base: "4", md: "5" }}
    >
      <Flex
        align={{ base: "stretch", lg: "center" }}
        direction={{ base: "column", lg: "row" }}
        gap="3"
        mb={children ? "4" : "0"}
      >
        <HStack spacing="3" align={description ? "start" : "center"} minW="0">
          <Flex
            boxSize="9"
            rounded="full"
            bg="smBackgroundSecondary"
            color="smPrimary"
            align="center"
            justify="center"
            flexShrink={0}
          >
            <Icon as={icon} boxSize="4" aria-hidden />
          </Flex>
          <Stack spacing="1" minW="0">
            <Text fontSize="sm" fontWeight="semibold" minW="0">
              {title}
            </Text>
            {description && (
              <Text fontSize="sm" color="smMuted">
                {description}
              </Text>
            )}
          </Stack>
        </HStack>

        {hasHeaderMeta && (
          <HStack
            spacing="3"
            align="center"
            justify={{ base: "flex-start", lg: "flex-end" }}
            flexWrap="wrap"
            ml={{ lg: "auto" }}
          >
            {badgeLabel && badgeStyle && (
              <Badge
                bg={badgeStyle.bg}
                color={badgeStyle.color}
                px="2"
                py="0.5"
                borderRadius="md"
                textTransform="none"
              >
                {badgeLabel}
              </Badge>
            )}
            {rightContent}
          </HStack>
        )}
      </Flex>

      {children && <Stack spacing="3">{children}</Stack>}
    </Box>
  );
};

const RemainingRequirement = ({
  requirement,
  onJumpToSupplier,
  onJumpToEspd,
  onJumpToQualification,
}: {
  requirement: PreflightRequirementDto;
  onJumpToSupplier: () => void;
  onJumpToEspd: () => void;
  onJumpToQualification: () => void;
}) => {
  const { t } = useTranslation();
  switch (requirement) {
    case PreflightRequirementDto.SupplierInfoIncomplete:
      return (
        <Text fontSize="sm">
          {t(
            "Supplier information, representative and self-declaration must be completed.",
          )}{" "}
          <Link color="smPrimary" onClick={onJumpToSupplier}>
            {t("Go to supplier information")}
          </Link>
        </Text>
      );
    case PreflightRequirementDto.EspdResponseIncomplete:
      return (
        <Text fontSize="sm">
          {t("Some ESPD answers are missing.")}{" "}
          <Link color="smPrimary" onClick={onJumpToEspd}>
            {t("Go to ESPD")}
          </Link>
        </Text>
      );
    case PreflightRequirementDto.QualificationResponseIncomplete:
      return (
        <Text fontSize="sm">
          {t("Some qualification requirements are unanswered.")}{" "}
          <Link color="smPrimary" onClick={onJumpToQualification}>
            {t("Go to qualification requirements")}
          </Link>
        </Text>
      );
    case PreflightRequirementDto.EspdFinalDeclarationIncomplete:
      return (
        <Text fontSize="sm">
          {t("The final ESPD declarations must be confirmed.")}
        </Text>
      );
    case PreflightRequirementDto.DeadlinePassed:
      return (
        <Text fontSize="sm">{t("The submission deadline has passed.")}</Text>
      );
    case PreflightRequirementDto.NotDraft:
      return (
        <Text fontSize="sm">{t("This offer has already been submitted.")}</Text>
      );
    default:
      return null;
  }
};
